import { Fragment, useMemo, useState } from "react"

import { useMatch } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { LayersIcon } from "@l2r-front/l2r-icons"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { BottomSheetHeights, useApplicationDispatchContext } from "@l2r-front/l2r-utils"

import photoImage from "../../assets/photo.png"
import streetviewImage from "../../assets/streetview.png"
import { MapLayerItem } from "../../components/MapLayerItem"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { MAP_ACTION_CHANGE_STYLE } from "../../constants/map"
import { IMAGE_COLLECTION_TYPE_PHOTO, IMAGE_COLLECTION_TYPE_STREETVIEW, MapStyles } from "../../constants/mapStyles"
import { useMapContext, useMapDispatchContext } from "../../contexts/MapContext"

import * as Styled from "./MapLayerSelector.styled"

const layersList = [
    {
        title: "containers.mapLayerSelector.details.title",
        id: "mapDetails",
        layers: [
            {
                value: IMAGE_COLLECTION_TYPE_PHOTO,
                field: IMAGE_COLLECTION_TYPE_PHOTO,
                id: IMAGE_COLLECTION_TYPE_PHOTO,
                image: photoImage,
                title: "containers.mapLayerSelector.details.photo",
            },
            {
                value: IMAGE_COLLECTION_TYPE_STREETVIEW,
                field: IMAGE_COLLECTION_TYPE_PHOTO,
                id: "streetview",
                image: streetviewImage,
                title: "containers.mapLayerSelector.details.streetview",
            },
        ],
    },
    {
        title: "containers.mapLayerSelector.mapStyles.title",
        id: "mapStyle",
        layers: [
            {
                action: MAP_ACTION_CHANGE_STYLE,
                field: "mapStyle",
                id: "mapStyle-plan",
                image: MapStyles.plan.background,
                title: "containers.mapLayerSelector.mapStyles.plan",
                value: MapStyles.plan,
            },
            {
                action: MAP_ACTION_CHANGE_STYLE,
                field: "mapStyle",
                id: "mapStyle-satellite",
                image: MapStyles.satellite.background,
                title: "containers.mapLayerSelector.mapStyles.satellite",
                value: MapStyles.satellite,
            },
        ],
    },
]

export const MapLayerSelector = (props) => {

    const { isBaseMap } = props

    const [anchorEl, setAnchorEl] = useState(null)
    const [mapState, mapDispatch] = useMapContext()
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()
    const { mapLayersState } = mapState
    const { setImageLayerDisplayed, setMapLayerState, setSelectedImageCollectionType } = mapDispatch
    const { setDesktopOpenPhoto, setMapHeightPct } = useMapDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const matchRoad = useMatch(":network/:app/:module/:road/*")

    const lists = useMemo(() => {
        return layersList.map((list, index) => {
            if (list.id === "mapDetails") {
                if (!matchRoad?.params?.road) {
                    return null
                }
            }
            if (!isBaseMap) {
                if (list.id === "mapDetails") {
                    return null
                }
            }
            return <Fragment key={list.id}>
                <div>
                    <Styled.Title
                        variant="H4"
                        isFirst={index === 0}>
                        {t(I18N_NAMESPACE, list.title)}
                    </Styled.Title>
                    <Styled.LayerList>
                        {list.layers.map(layer => {
                            const action = () => {
                                if (layer.field === IMAGE_COLLECTION_TYPE_PHOTO) {
                                    setSelectedImageCollectionType(layer.value)
                                    setImageLayerDisplayed(true)
                                    if (isMobile) {
                                        setMapHeightPct(50)
                                        setCurrentBottomSheetHeight(BottomSheetHeights.CLOSED)
                                    } else {
                                        setDesktopOpenPhoto(true)
                                    }
                                } else {
                                    setMapLayerState(layer.action, layer.value)
                                }
                                handlePopoverClose()
                            }

                            return <MapLayerItem
                                key={layer.id}
                                image={layer.image}
                                title={t(I18N_NAMESPACE, layer.title)}
                                isSelected={layer.field !== IMAGE_COLLECTION_TYPE_PHOTO && mapLayersState[layer.field] === layer.value}
                                onClick={action}
                            />
                        })}
                    </Styled.LayerList>
                </div>
                {layersList.length > 1 && index < layersList.length - 1 && <Styled.Divider />}
            </Fragment>
        })
    }, [isBaseMap, mapLayersState, matchRoad, setCurrentBottomSheetHeight, setImageLayerDisplayed, setMapLayerState, setMapHeightPct, setSelectedImageCollectionType, isMobile, setDesktopOpenPhoto, t])

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    return <>
        <Styled.Button
            color="cta-bg/cta-bg-primary"
            id="map-layers-open-button"
            onClick={handlePopoverOpen}
        >
            <LayersIcon />
        </Styled.Button>

        {!isMobile && <Styled.Container
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            onClose={handlePopoverClose}
            open={open}>
            <Styled.ContentWrapper>
                <Styled.List>
                    {lists}
                </Styled.List>
                <Styled.CloseButton onClick={handlePopoverClose}>
                    <Styled.CloseIcon />
                </Styled.CloseButton>
            </Styled.ContentWrapper>
        </Styled.Container >}
        {
            isMobile && <Styled.BottomSheet
                open={open}
                onClose={handlePopoverClose}>
                <Styled.ContentWrapper>
                    <Styled.List>
                        {lists}
                    </Styled.List>
                    <Styled.CloseButton onClick={handlePopoverClose}>
                        <Styled.CloseIcon />
                    </Styled.CloseButton>
                </Styled.ContentWrapper>
            </Styled.BottomSheet>
        }
    </>
}